import { stringIsNothing } from '../../utility/text';
import { adoptIssuedTabSession } from '../../utility/session-storage-keys';

import {
  AGWASuiteApiClientBase,
  ApiResponse,
  UserProfilePicture,
} from '../agwasuite-api';
import type {
  PerformInternalLoginInput,
  PerformInternalLoginResult,
  RegisterTwoFactorAuthenticationResponse,
  RequestPasswordResetInput,
  RequestPasswordResetResult,
  ResolveUserLoginTokenResponse,
  PasswordChangeRequiredInput,
  PasswordChangeRequiredResult,
  UpdateProfilePictureResult,
} from '../agwa-api-model';

export class ApiLogin extends AGWASuiteApiClientBase {
  constructor(baseUrl?: string) {
    super(baseUrl);
  }

  protected async loginExecute<TInput>(
    path: string,
    input: TInput
  ): Promise<ApiResponse<PerformInternalLoginResult>> {
    const result = await this.postExecute<TInput, PerformInternalLoginResult>(
      path,
      input
    );

    if (result.success && result.data?.success && result.data.token) {
      this.setAuthToken(
        undefined,
        result.data.loginCredentials?.loginUserId ?? ''
      );
      //using a cookie

      // The tab that signed in is handed its session on the login response, so it never has to ask
      // for one. Every OTHER tab does ask, at POST /api/TabSession/issue, because the auth cookie is
      // browser-wide and they never reach this path (#366).
      adoptIssuedTabSession(result.data.tabSessionId);
    }
    return result;
  }
  async login(
    input: PerformInternalLoginInput
  ): Promise<ApiResponse<PerformInternalLoginResult>> {
    return this.loginExecute('/api/login/internal', input);
  }

  async registerTwoFactorAuthentication(
    input: PerformInternalLoginInput
  ): Promise<ApiResponse<RegisterTwoFactorAuthenticationResponse>> {
    return this.postExecute<
      PerformInternalLoginInput,
      RegisterTwoFactorAuthenticationResponse
    >('/api/login/RegisterTwoFactorAuthentication', input);
  }

  async verify(): Promise<ApiResponse<ResolveUserLoginTokenResponse>> {
    return this.postExecute<void, ResolveUserLoginTokenResponse>(
      '/api/login/ResolveUserLoginToken',
      undefined
    );
  }
  async passwordChangeRequired(
    loginUserId?: string
  ): Promise<ApiResponse<PasswordChangeRequiredResult>> {
    return this.postExecute<
      PasswordChangeRequiredInput,
      PasswordChangeRequiredResult
    >('/api/login/PasswordChangeRequired', { loginUserId });
  }

  async requestPasswordReset(
    input: RequestPasswordResetInput
  ): Promise<ApiResponse<RequestPasswordResetResult>> {
    const result = await this.postExecute<
      RequestPasswordResetInput,
      RequestPasswordResetResult
    >('/api/login/RequestPasswordReset', input);

    return result;
  }

  protected async renewExecute(): Promise<
    ApiResponse<ResolveUserLoginTokenResponse>
  > {
    const result = await this.postExecute<{}, ResolveUserLoginTokenResponse>(
      '/api/login/renewAuthToken',
      {}
    );
    if (result.success && result.data?.token) {
      this._authToken = result.data.token;
    }
    return result;
  }

  async renew(): Promise<ApiResponse<ResolveUserLoginTokenResponse>> {
    return this.renewExecute();
  }

  async myProfilePicture(): Promise<ApiResponse<string>> {
    const result =
      await this.getExecute<UserProfilePicture>('/api/login/picture');
    if (result.success && !stringIsNothing(result.data?.pictureBase64)) {
      return {
        success: true,
        status: result.status,
        data: result.data.pictureBase64 ?? '',
      };
    } else {
      return {
        success: false,
        status: result.status,
        error: result.error ?? 'No profile image found',
      };
    }
  }

  async updateProfilePicture(
    pictureBase64: string
  ): Promise<ApiResponse<UpdateProfilePictureResult>> {
    return this.postExecute<
      { pictureBase64: string },
      UpdateProfilePictureResult
    >('/api/login/UpdateProfilePicture', { pictureBase64 });
  }

  async logout(): Promise<void> {
    const result = await this.postExecute<{}, {}>('/api/login/disconnect', {});
    if (result.success) {
      this.clearAuthToken();
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Multi-Session Management
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Gets all active sessions for this browser
   */
  async getSessions(): Promise<
    ApiResponse<import('../../datatypes/session-types').GetSessionsResult>
  > {
    return this.getExecute<
      import('../../datatypes/session-types').GetSessionsResult
    >('/api/login/GetSessions');
  }

  /**
   * Switches the active session to a different logged-in user
   */
  async switchSession(
    sessionId: string
  ): Promise<
    ApiResponse<import('../../datatypes/session-types').SwitchSessionResult>
  > {
    return this.postExecute<
      import('../../datatypes/session-types').SwitchSessionInput,
      import('../../datatypes/session-types').SwitchSessionResult
    >('/api/login/SwitchSession', { sessionId });
  }

  /**
   * Logs out a specific session
   */
  async logoutSession(sessionId: string): Promise<ApiResponse<void>> {
    return this.postExecute<
      import('../../datatypes/session-types').LogoutSessionInput,
      void
    >('/api/login/LogoutSession', { sessionId });
  }

  /**
   * Logs out all sessions
   */
  async logoutAllSessions(): Promise<ApiResponse<void>> {
    return this.postExecute<{}, void>('/api/login/LogoutAllSessions', {});
  }
}
